import { useMemo, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  ArrowLeftIcon,
  CreditCardIcon,
  BuildingLibraryIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import { PageHeader } from '@/components/layout';
import { Button, Card, CardHeader, Input, ContentLoader } from '@/components/common';
import { getErrorMessage } from '@/services/api';
import {
  gatewayUrlFrom,
  initiatePayment,
  issuePolicy,
  useIssuanceReadiness,
  type IssuePolicyResult,
} from './proposalFlow';

/**
 * BOARD #245 part 2 -- the PAYMENT step, and the issuance that follows it.
 *
 * WHAT WAS WRONG HERE. Two things, and the second is this codebase's signature
 * bug:
 *
 *  1. The payload was `{application_id, payment_method}`. `PaymentInitiateView`
 *     requires `quote_id` AND `success_url` and 400s without either, so the
 *     gateway branch could never have reached an insurer.
 *  2. The `own_pg` branch called `toast.success('Payment recorded successfully')`
 *     and navigated on -- unconditionally, on the response of a request that
 *     recorded nothing. No payment row, no reference stored, no policy. An
 *     action that reports success having achieved nothing is exactly the defect
 *     this platform keeps shipping, so that branch has been rebuilt rather than
 *     patched.
 *
 * WHAT IT DOES NOW. The route param is the OPTION id (`Quote.id`), because at
 * this point in the flow no policy exists: `PolicyIssueView` requires
 * `payment_id` and `payment_amount`, so issuance is what happens AFTER payment,
 * not before it. Both branches end in a real call:
 *
 *  * Insurer gateway -- `POST /integrations/payment/initiate/` with the two
 *    required fields. The customer is redirected only if the adapter actually
 *    returned somewhere to send them; when it did not, the raw answer is shown
 *    and nothing is claimed.
 *  * Paid outside the platform -- the reference is carried straight into
 *    `POST /integrations/policies/issue/` as `payment_id`, which is what makes
 *    the insurer issue and (since board #255) what writes the `Policy` row. The
 *    screen then reports what the SERVER says it persisted: `policy_number` is
 *    nullable on purpose and null means "the insurer issued but this platform
 *    has no local record", which is shown as such.
 *
 * The route is `/quotes/payment/:policyNumber` (App.tsx:248). THAT PARAM NAME IS
 * NOW WRONG -- it carries a quote option id. Renaming it is an App.tsx edit,
 * which is outside this change's file domain; it is read under its real meaning
 * here and the rename has been reported.
 */

type PaymentMethod = 'insurer_gateway' | 'paid_outside' | null;

export function PaymentPage() {
  // See the note above: the route still spells this `:policyNumber`, but what
  // arrives is a `Quote.id`.
  const { policyNumber: quoteId } = useParams<{ policyNumber: string }>();
  const navigate = useNavigate();
  const [selectedMethod, setSelectedMethod] = useState<PaymentMethod>(null);
  const [transactionRef, setTransactionRef] = useState('');
  const [busy, setBusy] = useState(false);
  const [gatewayAnswer, setGatewayAnswer] = useState<Record<string, unknown> | null>(null);
  const [issueResult, setIssueResult] = useState<IssuePolicyResult | null>(null);
  const [failure, setFailure] = useState<string>('');

  const { data: readiness, isLoading, error: readinessError } = useIssuanceReadiness(quoteId);

  const canUseGateway = !!readiness?.can_issue_via_api && !!readiness?.supports_payment_initiation;
  const canIssue = !!readiness?.can_issue_via_api;

  const blockers = useMemo(() => readiness?.blockers ?? [], [readiness]);

  const handleGateway = async () => {
    setBusy(true);
    setFailure('');
    setGatewayAnswer(null);
    try {
      const result = await initiatePayment({
        quote_id: quoteId!,
        success_url: `${window.location.origin}/quotes/payment/${quoteId}`,
        cancel_url: `${window.location.origin}/quotes/payment/${quoteId}`,
        ...(readiness?.environment ? { environment: readiness.environment } : {}),
      });
      const url = gatewayUrlFrom(result);
      if (url) {
        window.location.href = url;
        return;
      }
      // The call succeeded and gave us nowhere to send the customer. Show what
      // came back; never navigate on as though a payment had been taken.
      setGatewayAnswer(result as Record<string, unknown>);
    } catch (error) {
      setFailure(getErrorMessage(error));
    } finally {
      setBusy(false);
    }
  };

  const handlePaidOutside = async () => {
    if (!transactionRef.trim()) {
      toast.error('Enter the transaction reference you took the payment against');
      return;
    }
    if (!readiness?.insurer_id || !readiness?.environment) {
      setFailure('This option has no insurer integration to issue against.');
      return;
    }

    setBusy(true);
    setFailure('');
    setIssueResult(null);
    try {
      const result = await issuePolicy({
        quote_id: quoteId!,
        insurer_code: readiness.insurer_code,
        insurer_id: readiness.insurer_id,
        // Sent explicitly: `PolicyIssueSerializer.environment` DEFAULTS to
        // sandbox, so a broker who is live in production would otherwise be
        // routed into a configuration that does not exist and told the
        // integration was missing.
        environment: readiness.environment,
        payment_id: transactionRef.trim(),
        payment_amount: readiness.total_premium,
        payment_mode: 'offline',
        transaction_reference: transactionRef.trim(),
      });
      setIssueResult(result);
      // THE CLAIM IS DERIVED, NEVER ASSERTED. `PolicyIssueView` returns
      // `policy_number: null` when it could not re-read the row it was told was
      // written; saying "issued" then would be inventing a policy.
      if (result.policy_number) {
        toast.success(`Policy ${result.policy_number} recorded`);
        navigate(`/quotes/status/${result.policy_number}`);
      }
    } catch (error) {
      setFailure(getErrorMessage(error));
    } finally {
      setBusy(false);
    }
  };

  if (isLoading) {
    return <ContentLoader />;
  }

  // The option could not be read at all -- a 404 for a quote that is not this
  // broker's, or a deleted one. Without this the page rendered two disabled
  // buttons and no reason, which is the same silence the rest of this flow was
  // built out of.
  if (readinessError || !readiness) {
    return (
      <div className="text-center py-12">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white">This quote option could not be loaded</h2>
        <p className="text-gray-500 dark:text-gray-400 mt-2 max-w-xl mx-auto">
          {readinessError
            ? getErrorMessage(readinessError)
            : 'The server returned nothing for this option.'}
        </p>
        <Link to="/quotes" className="mt-4 inline-block">
          <Button variant="outline" leftIcon={<ArrowLeftIcon className="w-5 h-5" />}>
            Back to Quotes
          </Button>
        </Link>
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title="Payment"
        subtitle={readiness ? `${readiness.insurer_name} — ${readiness.quote_number}` : ''}
        breadcrumbs={[
          { label: 'Quotes', href: '/quotes' },
          { label: 'Payment' },
        ]}
        actions={
          <Link to="/quotes">
            <Button variant="outline" leftIcon={<ArrowLeftIcon className="w-5 h-5" />}>
              Back to Quotes
            </Button>
          </Link>
        }
      />

      {/* Everything that stands between this option and an API issuance, said
          in the server's own words before anything is attempted. */}
      {blockers.length > 0 && (
        <Card className="mb-6 border-yellow-200 dark:border-yellow-800 bg-yellow-50 dark:bg-yellow-900/20">
          <div className="flex items-start gap-3">
            <ExclamationTriangleIcon className="w-5 h-5 text-yellow-500 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-yellow-800 dark:text-yellow-300">
                This option cannot be paid for or issued through the insurer
              </p>
              <ul className="mt-2 space-y-1">
                {blockers.map((blocker) => (
                  <li key={blocker} className="text-xs text-yellow-700 dark:text-yellow-300">
                    {blocker}
                  </li>
                ))}
              </ul>
              <p className="text-xs text-yellow-700 dark:text-yellow-300 mt-2">
                Take the payment as you normally would and upload the issued policy copy on the
                quote; that is what creates the policy record.
              </p>
              <Link to="/quotes" className="inline-block mt-3">
                <Button variant="outline" size="sm">
                  Back to the quote
                </Button>
              </Link>
            </div>
          </div>
        </Card>
      )}

      {failure && (
        <Card className="mb-6 border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20">
          <p className="text-sm font-medium text-red-800 dark:text-red-300">The insurer refused the request</p>
          <p className="text-sm text-red-700 dark:text-red-300 mt-1">{failure}</p>
        </Card>
      )}

      {/* The gateway answered, but with no link. Neither a success nor an
          error -- so it is shown as neither. */}
      {gatewayAnswer && (
        <Card className="mb-6 border-yellow-200 dark:border-yellow-800 bg-yellow-50 dark:bg-yellow-900/20">
          <p className="text-sm font-medium text-yellow-800 dark:text-yellow-300">
            No payment link came back
          </p>
          <p className="text-xs text-yellow-700 dark:text-yellow-300 mt-1">
            The insurer accepted the request but did not return a URL to send the customer to.
            Nothing has been paid. Their answer, unaltered:
          </p>
          <pre className="mt-2 text-xs text-yellow-900 dark:text-yellow-200 bg-yellow-100 dark:bg-yellow-900/30 rounded p-2 overflow-x-auto">
            {JSON.stringify(gatewayAnswer, null, 2)}
          </pre>
        </Card>
      )}

      {/* Issued, but this platform could not thread it to a policy row. The
          money is real and the record is missing; say exactly that. */}
      {issueResult && !issueResult.policy_number && (
        <Card className="mb-6 border-yellow-200 dark:border-yellow-800 bg-yellow-50 dark:bg-yellow-900/20">
          <p className="text-sm font-medium text-yellow-800 dark:text-yellow-300">Issued, with no local policy record</p>
          <p className="text-xs text-yellow-700 dark:text-yellow-300 mt-1">
            {issueResult.message ??
              'The insurer reported the policy issued, but no policy row could be created on this platform. Do not retry — that would ask the insurer to issue it twice. Raise it with support with the reference below.'}
          </p>
          {issueResult.insurer_policy_number && (
            <p className="text-xs text-yellow-900 dark:text-yellow-200 mt-2">
              Insurer policy number: {issueResult.insurer_policy_number}
            </p>
          )}
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Payment Methods */}
        <div className="lg:col-span-2 space-y-6">
          <Card>
            <CardHeader title="Select Payment Method" subtitle="Choose how the premium is being paid" />

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {/* Insurer's own gateway */}
              <button
                type="button"
                disabled={!canUseGateway}
                onClick={() => setSelectedMethod('insurer_gateway')}
                className={`p-6 rounded-xl border-2 text-left transition-all ${
                  !canUseGateway
                    ? 'border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/50 opacity-60 cursor-not-allowed'
                    : selectedMethod === 'insurer_gateway'
                    ? 'border-primary-600 bg-primary-50 dark:bg-primary-900/20 ring-2 ring-primary-200 dark:ring-primary-800'
                    : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700/50'
                }`}
              >
                <div className="flex items-center gap-3 mb-3">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                    selectedMethod === 'insurer_gateway' ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-600 dark:text-primary-400' : 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400'
                  }`}>
                    <CreditCardIcon className="w-5 h-5" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-gray-900 dark:text-white">
                      {readiness?.insurer_name || 'Insurer'} gateway
                    </p>
                  </div>
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {canUseGateway
                    ? 'The customer is redirected to the insurer to pay. We only send them if the insurer returns a link.'
                    : 'Not available for this option — see the reasons above.'}
                </p>
              </button>

              {/* Paid outside the platform */}
              <button
                type="button"
                disabled={!canIssue}
                onClick={() => setSelectedMethod('paid_outside')}
                className={`p-6 rounded-xl border-2 text-left transition-all ${
                  !canIssue
                    ? 'border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/50 opacity-60 cursor-not-allowed'
                    : selectedMethod === 'paid_outside'
                    ? 'border-primary-600 bg-primary-50 dark:bg-primary-900/20 ring-2 ring-primary-200 dark:ring-primary-800'
                    : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700/50'
                }`}
              >
                <div className="flex items-center gap-3 mb-3">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                    selectedMethod === 'paid_outside' ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-600 dark:text-primary-400' : 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400'
                  }`}>
                    <BuildingLibraryIcon className="w-5 h-5" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-gray-900 dark:text-white">Already paid</p>
                  </div>
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {canIssue
                    ? 'Payment was taken elsewhere. The reference is sent to the insurer to issue against.'
                    : 'Not available for this option — see the reasons above.'}
                </p>
              </button>
            </div>
          </Card>

          {selectedMethod === 'paid_outside' && (
            <Card>
              <CardHeader
                title="Transaction Details"
                subtitle="The reference the insurer will be asked to issue against"
              />
              <Input
                // `Input` derives its element id from `id || name`; with neither,
                // htmlFor points at nothing, so the label is not associated with
                // the field for a screen reader and clicking it does not focus.
                id="payment-transaction-ref"
                name="transaction_ref"
                label="Transaction Reference Number"
                placeholder="Enter reference number..."
                value={transactionRef}
                onChange={(e) => setTransactionRef(e.target.value)}
                required
              />
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                This is sent to the insurer as the payment id. It is not a payment: nothing on this
                platform moves money.
              </p>
            </Card>
          )}

          <div className="flex justify-end">
            <Button
              onClick={selectedMethod === 'insurer_gateway' ? handleGateway : handlePaidOutside}
              isLoading={busy}
              disabled={!selectedMethod || busy}
              size="lg"
            >
              {selectedMethod === 'insurer_gateway' ? 'Get payment link' : 'Issue policy'}
            </Button>
          </div>
        </div>

        {/* Premium Summary Sidebar */}
        <div>
          <Card>
            <CardHeader title="Premium Summary" />
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-500 dark:text-gray-400">Quote</span>
                <span className="text-sm font-medium text-gray-900 dark:text-white">{readiness?.quote_number ?? '--'}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-500 dark:text-gray-400">Insurer</span>
                <span className="text-sm font-medium text-gray-900 dark:text-white">{readiness?.insurer_name ?? '--'}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-500 dark:text-gray-400">Premium</span>
                {/* Off the stored quote. NEVER defaulted: a premium invented on
                    this screen is a number the customer would be charged. */}
                <span className="text-sm font-semibold text-gray-900 dark:text-white">
                  {readiness?.total_premium ?? '--'}
                </span>
              </div>
              {readiness?.environment && (
                <div className="flex justify-between items-center border-t border-gray-200 dark:border-gray-700 pt-3">
                  <span className="text-sm text-gray-500 dark:text-gray-400">Environment</span>
                  <span className="text-sm font-medium text-gray-900 dark:text-white">{readiness.environment}</span>
                </div>
              )}
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}
